<template>
    <div class="vendor-chatbot-wrapper">
        <section v-if="msg.type === 'vendor_list'" class="vendor-card">
            <header class="vendor-card__header">
                <div>
                    <h4>{{ vendorData.heading || 'Available Vendors' }}</h4>
                    <p>{{ vendors.length }} vendor{{ vendors.length === 1 ? '' : 's' }} found</p>
                </div>
                <span class="vendor-count">{{ vendors.length }}</span>
            </header>

            <div v-if="vendors.length" class="vendor-card__body">
                <div v-for="(vendor, index) in vendors"
                     :key="vendor.vendorId || vendor.id || index"
                     class="vendor-item">
                    <div class="vendor-item__top">
                        <div class="vendor-main">
                            <div class="vendor-avatar">{{ getInitials(vendor.vendorName) }}</div>
                            <div class="vendor-main__text">
                                <div class="vendor-name">{{ vendor.vendorName || 'Vendor' }}</div>
                            </div>
                        </div>

                        <div class="vendor-actions">
                            <button type="button"
                                    class="vendor-details-btn"
                                    :disabled="disabled"
                                    @click="toggleDetails(vendor, index)">
                                {{ isExpanded(vendor, index) ? 'Hide Details' : 'Details' }}
                            </button>

                            <button type="button"
                                    :class="[
                                        'vendor-status-btn',
                                        isActive(vendor) ? 'vendor-status-btn--active' : 'vendor-status-btn--inactive'
                                    ]"
                                    :disabled="disabled"
                                    @click="askStatusChange(vendor)">
                                {{ isActive(vendor) ? 'Active' : 'Inactive' }}
                            </button>
                        </div>
                    </div>

                    <div v-if="isExpanded(vendor, index)" class="vendor-details">
                        <div class="vendor-detail-row"><span>Work Type</span><b>{{ vendor.workType || '—' }}</b></div>
                        <div class="vendor-detail-row"><span>Email</span><b>{{ vendor.email || '—' }}</b></div>
                        <div class="vendor-detail-row"><span>Phone</span><b>{{ vendor.phone || '—' }}</b></div>
                        <div class="vendor-detail-row"><span>Service</span><b>{{ vendor.serviceTitle || '—' }}</b></div>
                        <div class="vendor-detail-row"><span>Company</span><b>{{ vendor.companyName || '—' }}</b></div>
                        <div class="vendor-detail-row vendor-detail-row--last">
                            <span>Created</span>
                            <b>{{ formatMMDDYYYY(vendor.createdOn) }}</b>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="vendor-empty">No vendors found.</div>
        </section>

        <section v-else-if="msg.type === 'vendor_list_failed'" class="vendor-card vendor-result-card">
            <header class="vendor-card__header vendor-card__header--error">
                <div><h4>Vendors</h4><p>Unable to load vendor list.</p></div>
            </header>
            <div class="vendor-result-card__body vendor-error">
                {{ responseText || 'Currently unable to retrieve vendor list. Please try again.' }}
            </div>
        </section>

        <section v-else-if="msg.type === 'vendor_status_updated'" class="vendor-card vendor-result-card">
            <header class="vendor-card__header">
                <div><h4>Vendor Status Updated</h4><p>The vendor status has been updated.</p></div>
            </header>
            <div class="vendor-result-card__body">
                {{ responseText || 'Vendor status updated successfully.' }}
            </div>
            <div class="vendor-result-card__footer">
                <span>{{ formatMMDDYYYY(currentDate) }}</span>
                <span class="vendor-ok">Updated</span>
            </div>
        </section>

        <section v-else-if="msg.type === 'vendor_status_update_failed'" class="vendor-card vendor-result-card">
            <header class="vendor-card__header vendor-card__header--error">
                <div><h4>Vendor Status</h4><p>Unable to update vendor status.</p></div>
            </header>
            <div class="vendor-result-card__body vendor-error">
                {{ responseText || 'Unable to update vendor status. Please try again.' }}
            </div>
        </section>

        <div v-else class="vendor-text-card">{{ responseText }}</div>

        <div v-if="statusConfirm.show" class="vendor-confirm-overlay" @click.self="closeStatusConfirm">
            <div class="vendor-confirm-modal">
                <div class="vendor-confirm-modal__header">
                    {{ statusConfirm.newStatus ? 'Activate Vendor' : 'Deactivate Vendor' }}
                </div>
                <div class="vendor-confirm-modal__body">
                    Are you sure you want to mark
                    <strong>{{ statusConfirm.vendorName }}</strong>
                    as {{ statusConfirm.newStatus ? 'active' : 'inactive' }}?
                </div>
                <div class="vendor-confirm-modal__actions">
                    <button type="button" class="vendor-confirm-cancel" @click="closeStatusConfirm">Cancel</button>
                    <button type="button" class="vendor-confirm-ok" @click="confirmStatusChange">OK</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    const formatMMDDYYYY = (value) => {
        if (!value) return '—';

        const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);

        if (match) {
            return `${match[2]}-${match[3]}-${match[1]}`;
        }

        return String(value);
    };

    export default {
        name: 'VendorChatbot',

        props: {
            msg: { type: Object, required: true },
            disabled: { type: Boolean, default: false },
            currentDate: { type: String, default: '' },
        },

        emits: ['toggle-status'],

        data() {
            return {
                expandedVendors: {},
                statusConfirm: {
                    show: false,
                    vendor: null,
                    vendorId: '',
                    vendorName: '',
                    currentStatus: false,
                    newStatus: false,
                },
            };
        },

        computed: {
            payload() {
                return this.msg?.payload || {};
            },

            vendorData() {
                return this.payload?.vendor_data || {};
            },

            vendors() {
                const data = this.vendorData;
                if (Array.isArray(data)) return data;
                if (Array.isArray(data.vendors)) return data.vendors;
                if (Array.isArray(data.data)) return data.data;
                if (Array.isArray(data.items)) return data.items;
                if (Array.isArray(data.result)) return data.result;
                return [];
            },

            responseText() {
                return this.payload?.response || this.msg?.text || '';
            },
        },

        methods: {
            formatMMDDYYYY,

            getVendorKey(vendor, index) {
                return String(vendor?.vendorId || vendor?.VendorId || vendor?.id || index);
            },

            getInitials(name) {
                const parts = String(name || 'V').trim().split(/\s+/).filter(Boolean);
                if (!parts.length) return 'V';
                if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
                return (String(parts[0][0] || '') + String(parts[1][0] || '')).toUpperCase();
            },

            isActive(vendor) {
                return vendor?.isActive === true || vendor?.IsActive === true;
            },

            isExpanded(vendor, index) {
                return this.expandedVendors[this.getVendorKey(vendor, index)] === true;
            },

            toggleDetails(vendor, index) {
                if (this.disabled) return;
                const key = this.getVendorKey(vendor, index);
                this.expandedVendors = {
                    ...this.expandedVendors,
                    [key]: !this.expandedVendors[key],
                };
            },

            askStatusChange(vendor) {
                if (!vendor || this.disabled) return;

                const vendorId = vendor.vendorId || vendor.VendorId || vendor.id || '';
                if (!vendorId) return;

                const currentStatus = this.isActive(vendor);

                this.statusConfirm = {
                    show: true,
                    vendor,
                    vendorId: String(vendorId),
                    vendorName: vendor.vendorName || vendor.VendorName || 'Vendor',
                    currentStatus,
                    newStatus: !currentStatus,
                };
            },

            closeStatusConfirm() {
                this.statusConfirm = {
                    show: false,
                    vendor: null,
                    vendorId: '',
                    vendorName: '',
                    currentStatus: false,
                    newStatus: false,
                };
            },

            confirmStatusChange() {
                if (this.disabled || !this.statusConfirm.vendorId) return;

                const payload = {
                    vendor: this.statusConfirm.vendor,
                    vendorId: this.statusConfirm.vendorId,
                    vendorName: this.statusConfirm.vendorName,
                    isActive: this.statusConfirm.newStatus,
                    currentStatus: this.statusConfirm.currentStatus,
                };

                this.closeStatusConfirm();
                this.$emit('toggle-status', payload);
            },
        },
    };</script>

<style scoped>
    .vendor-chatbot-wrapper {
        width: 100%;
        max-width: 390px;
        font-family: inherit;
        box-sizing: border-box;
    }

    .vendor-card {
        width: 100%;
        min-width: 300px;
        max-width: 390px;
        color: #1e2d45;
        background: #fff;
        border-radius: 14px;
        overflow: hidden;
        box-shadow: 0 4px 18px rgba(15,23,42,.12);
        box-sizing: border-box;
    }

    .vendor-card__header {
        padding: 14px 16px;
        color: #fff;
        background: linear-gradient(135deg,#0f66b8,#122341);
        display: flex;
        justify-content: space-between;
        gap: 10px;
        align-items: center;
    }

    .vendor-card__header--error {
        background: linear-gradient(135deg,#991b1b,#dc2626);
    }

    .vendor-card__header h4 {
        margin: 0;
        font-size: 14px;
        line-height: 1.3;
        font-weight: 700;
    }

    .vendor-card__header p {
        margin: 3px 0 0;
        font-size: 11px;
        line-height: 1.4;
        opacity: .84;
    }

    .vendor-count {
        min-width: 30px;
        padding: 4px 8px;
        border: 1px solid rgba(255,255,255,.3);
        border-radius: 14px;
        background: rgba(255,255,255,.12);
        color: #fff;
        font-size: 11px;
        font-weight: 700;
        text-align: center;
        box-sizing: border-box;
    }

    .vendor-card__body {
        padding: 4px 16px 10px;
        max-height: 360px;
        overflow-y: auto;
        box-sizing: border-box;
    }

        .vendor-card__body::-webkit-scrollbar {
            width: 5px;
        }

        .vendor-card__body::-webkit-scrollbar-track {
            background: transparent;
        }

        .vendor-card__body::-webkit-scrollbar-thumb {
            background: #c7d0dc;
            border-radius: 10px;
        }

    .vendor-item {
        padding: 12px 0;
        border-bottom: 1px solid #edf0f3;
    }

        .vendor-item:last-child {
            border-bottom: 0;
        }

    .vendor-item__top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
    }

    .vendor-main {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
        flex: 1;
    }

    .vendor-avatar {
        flex: 0 0 36px;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: #122341;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 700;
        box-sizing: border-box;
    }

    .vendor-main__text {
        min-width: 0;
        flex: 1;
    }

    .vendor-name {
        color: #1e2d45;
        font-size: 14px;
        line-height: 1.4;
        font-weight: 700;
        word-break: break-word;
    }

    .vendor-actions {
        flex: 0 0 auto;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 5px;
    }

    .vendor-details-btn {
        padding: 4px 9px;
        border: 1px solid #cbd5e1;
        border-radius: 12px;
        background: #f8fafc;
        color: #1e2d45;
        font-size: 11px;
        line-height: 1.2;
        font-weight: 600;
        cursor: pointer;
    }

    .vendor-status-btn {
        padding: 3px 9px;
        border-radius: 12px;
        font-size: 11px;
        line-height: 1.2;
        font-weight: 700;
        cursor: pointer;
    }

    .vendor-status-btn--active {
        border: 1px solid rgba(22,163,74,.35);
        background: rgba(22,163,74,.08);
        color: #16a34a;
    }

    .vendor-status-btn--inactive {
        border: 1px solid rgba(220,38,38,.35);
        background: rgba(220,38,38,.08);
        color: #dc2626;
    }

    .vendor-details {
        margin-top: 10px;
        padding: 12px 14px;
        background: #f6f7f9;
        border: 1px solid #e2e6eb;
        border-radius: 9px;
        box-sizing: border-box;
    }

    .vendor-detail-row {
        display: grid;
        grid-template-columns: 90px minmax(0, 1fr);
        align-items: start;
        column-gap: 12px;
        padding: 8px 0;
        border-bottom: 1px solid #dfe4ea;
        font-size: 12px;
        line-height: 1.45;
    }

    .vendor-detail-row--last {
        border-bottom: 0;
    }

    .vendor-detail-row > span {
        min-width: 0;
        color: #667085;
        font-size: 12px;
        font-weight: 700;
    }

    .vendor-detail-row > b {
        min-width: 0;
        color: #1e2d45;
        font-size: 12px;
        font-weight: 600;
        text-align: right;
        word-break: normal;
        overflow-wrap: anywhere;
    }

    .vendor-empty {
        padding: 24px 16px;
        color: #8b93a1;
        font-size: 12px;
        text-align: center;
    }

    .vendor-result-card__body {
        padding: 16px;
        color: #1e2d45;
        font-size: 12px;
        line-height: 1.5;
    }

    .vendor-result-card__footer {
        padding: 10px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: #f8f6f2;
        border-top: 1px solid #e5e7eb;
        font-size: 11px;
    }

    .vendor-ok {
        color: #16a34a;
        font-weight: 700;
    }

    .vendor-error {
        color: #b42318;
    }

    .vendor-text-card {
        width: fit-content;
        min-width: 0;
        max-width: 390px;
        padding: 12px 16px;
        background: #fff;
        color: #333;
        border-radius: 6px 16px 16px 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,.06);
        box-sizing: border-box;
        font-size: 13px;
        line-height: 1.5;
        word-break: break-word;
        overflow-wrap: anywhere;
    }

    .vendor-confirm-overlay {
        position: fixed;
        inset: 0;
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        background: rgba(0,0,0,.5);
        box-sizing: border-box;
    }

    .vendor-confirm-modal {
        width: 100%;
        max-width: 360px;
        background: #fff;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 8px 30px rgba(0,0,0,.3);
    }

    .vendor-confirm-modal__header {
        padding: 14px 18px;
        background: #122341;
        color: #fff;
        font-size: 14px;
        font-weight: 700;
    }

    .vendor-confirm-modal__body {
        padding: 20px 18px;
        color: #1e2d45;
        font-size: 13px;
        line-height: 1.5;
    }

    .vendor-confirm-modal__actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 12px 18px 16px;
    }

    .vendor-confirm-cancel,
    .vendor-confirm-ok {
        min-width: 78px;
        padding: 8px 14px;
        border: 0;
        border-radius: 7px;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
    }

    .vendor-confirm-cancel {
        background: #f1f5f9;
        color: #1e2d45;
    }

    .vendor-confirm-ok {
        background: #0f66b8;
        color: #fff;
    }

    button:disabled {
        opacity: .55;
        cursor: not-allowed;
    }

    @media (max-width: 480px) {
        .vendor-chatbot-wrapper,
        .vendor-card,
        .vendor-text-card {
            max-width: 100%;
        }

        .vendor-card {
            min-width: 0;
        }

        .vendor-card__body {
            max-height: 320px;
            padding-left: 12px;
            padding-right: 12px;
        }

        .vendor-details {
            padding: 10px 12px;
        }
    }
</style>
